import {
    Box,
    Code,
    HStack,
    Icon,
    Link,
    ListItem,
    Text,
    UnorderedList,
} from '@chakra-ui/react';
import { PlusCircle } from '@phosphor-icons/react/PlusCircle';
import { Swap } from '@phosphor-icons/react/Swap';
import { Wrench } from '@phosphor-icons/react/Wrench';

import React from 'react';
import type {
    PublicChangelogEntry,
    PublicChangelogInline,
} from './public-changelog-config';

type TitleType =
    | 'Added'
    | 'Changed'
    | 'Fixed'
    | 'Deprecated'
    | 'Removed'
    | 'Security';

interface TitleConfig {
    icon?: React.ElementType; // optional: Some we never use and not worth to waste more bytes load unused icons
    color: string;
}

const TITLE_MAP: Record<TitleType, TitleConfig> = {
    Added: { icon: PlusCircle, color: 'green' },
    Changed: { icon: Swap, color: 'blue' },
    Fixed: { icon: Wrench, color: 'orange' },
    Deprecated: { color: 'yellow' },
    Removed: { color: 'gray' },
    Security: { color: 'blue' },
};

export const ChangesSection = ({
    title,
    changes,
}: {
    title: TitleType;
    changes?: PublicChangelogEntry[];
}) => {
    if (!changes || changes.length === 0) return null;

    const { icon, color } = TITLE_MAP[title];
    const colorStyles = {
        color: `${color}.500`,
        _dark: { color: `${color}.300` },
    };

    return (
        <HStack
            align="stretch"
            _hover={{ bg: 'blackAlpha.50', _dark: { bg: 'whiteAlpha.50' } }}
        >
            <HStack h="100%" pt={0.5} {...colorStyles}>
                {icon && <Icon as={icon} fontSize="1.2em" />}
                <Text w="100px" flexShrink={0} fontWeight="700" fontSize="sm">
                    {title}
                </Text>
            </HStack>
            <UnorderedList spacing={2}>
                {changes.map((change, index) => (
                    <ListItem key={index}>
                        <RichText nodes={change} />
                    </ListItem>
                ))}
            </UnorderedList>
        </HStack>
    );
};

const RichText = ({ nodes }: { nodes: PublicChangelogInline[] }) => (
    <>
        {nodes.map((node, index) => (
            <RichTextNode key={index} node={node} />
        ))}
    </>
);

const RichTextNode = ({ node }: { node: PublicChangelogInline }) => {
    switch (node.type) {
        case 'text':
            return node.value;
        case 'strong':
            return (
                <Box as="strong">
                    <RichText nodes={node.children} />
                </Box>
            );
        case 'emphasis':
            return (
                <Box as="em">
                    <RichText nodes={node.children} />
                </Box>
            );
        case 'code':
            return <Code>{node.value}</Code>;
        case 'link':
            return (
                <Link href={node.href} isExternal textDecoration="underline">
                    <RichText nodes={node.children} />
                </Link>
            );
    }
};
